import React, { useState, useEffect, useMemo } from 'react';
import { TruckIcon, FunnelIcon, ArrowPathIcon, CurrencyDollarIcon, ClockIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useXmlCtes, useXmlCteStats } from '../hooks/useCte';
import type { XmlCteFilters } from '../types/cte';
import CteList from '../components/cte/CteList';
import CteModal from '../components/cte/CteModal';
import type { XmlCte } from '../types/cte';
import { money } from '../utils/format';
import { btnPrimario, btnSecundario, inputIris } from '../components/ui/iris';

const CteMonitoringPage: React.FC = () => {
  const [filters, setFilters] = useState<XmlCteFilters>({ dias: 30 }); // Padrão 30 dias
  const [selectedCte, setSelectedCte] = useState<XmlCte | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Aplicar filtros automaticamente quando searchTerm muda
  const activeFilters = useMemo(() => {
    return {
      ...filters,
      search: searchTerm || undefined
    };
  }, [filters, searchTerm]);

  const { 
    data: response, 
    isLoading, 
    error, 
    refetch 
  } = useXmlCtes(activeFilters, currentPage, itemsPerPage);

  const { data: stats } = useXmlCteStats(activeFilters);

  const xmlCtes = response?.data.data || [];
  const totalPages = response?.data.pagination?.totalPages || 1;
  const totalItems = response?.data.pagination?.totalItems || 0;
  const startIndex = ((response?.data.pagination?.page || 1) - 1) * (response?.data.pagination?.pageSize || itemsPerPage);

  // Reset da página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, itemsPerPage]);

  const handleRefresh = () => {
    refetch();
  };

  const handleOpenModal = (cte: XmlCte) => {
    setSelectedCte(cte);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCte(null);
    setIsModalOpen(false);
  };



  if (error) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-4">
        <div className="bg-danger-soft border border-line rounded-iris-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-danger">
                Erro ao carregar CTes
              </h3>
              <p className="text-sm text-danger mt-1">
                Não foi possível carregar os dados dos CTes.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <TruckIcon className="h-7 w-7 text-accent-text flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">CTe de Terceiros</h1>
            <p className="text-xs text-tx-2">
              Gerenciar conhecimentos de transporte eletrônicos
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 bg-surface-2 hover:bg-surface rounded-iris transition-colors disabled:opacity-50"
            title="Atualizar lista"
          >
            <ArrowPathIcon className={`h-5 w-5 text-tx-2 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-iris transition-colors ${
              showFilters ? 'bg-accent-soft text-accent-text' : 'bg-surface-2 hover:bg-surface text-tx-2'
            }`}
            title="Filtros avançados"
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">Total de CTes</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-tx">{stats?.total || 0}</p>
              </div>
              <div className="bg-accent-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <TruckIcon className="h-4 w-4 lg:h-6 lg:w-6 text-accent-text" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">Valor Total</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-ok">{money(stats?.valorTotal)}</p>
              </div>
              <div className="bg-ok-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <CurrencyDollarIcon className="h-4 w-4 lg:h-6 lg:w-6 text-ok" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">Última Atualização</p>
                <p className="text-sm lg:text-base font-medium text-tx">
                  {stats?.ultimaAtualizacao ? new Date(stats.ultimaAtualizacao).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A'}
                </p>
              </div>
              <div className="bg-surface-2 p-2 lg:p-3 rounded-full flex-shrink-0">
                <ClockIcon className="h-4 w-4 lg:h-6 lg:w-6 text-tx-2" />
              </div>
            </div>
          </div>
        </div>

        {/* Busca e Filtros */}
        <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-4 lg:p-6">
          <div className="space-y-4">
            {/* Busca Principal */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-tx-3 z-10" />
                  <input
                    type="text"
                    placeholder="Buscar por emitente, remetente, destinatário, chave ou número..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${inputIris} w-full pl-10`}
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* Filtros de Período */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Hoje', value: 1 },
                    { label: '7 dias', value: 7 },
                    { label: '15 dias', value: 15 },
                    { label: '30 dias', value: 30 },
                    { label: '60 dias', value: 60 },
                    { label: '90 dias', value: 90 },
                    { label: 'Personalizado', value: null }
                  ].map((periodo) => (
                    <button
                      key={periodo.label}
                      onClick={() => {
                        if (periodo.value === null) {
                          // Abrir filtro de datas personalizado
                          const hoje = new Date();
                          const dataFimDefault = hoje.toISOString().split('T')[0];
                          const trintaDiasAtras = new Date();
                          trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30);
                          const dataInicioDefault = trintaDiasAtras.toISOString().split('T')[0];
                          
                          setFilters(prev => ({ 
                            ...prev, 
                            dias: null,
                            dataInicio: prev.dataInicio || dataInicioDefault,
                            dataFim: prev.dataFim || dataFimDefault
                          }));
                        } else {
                          setFilters(prev => ({ 
                            ...prev, 
                            dias: periodo.value,
                            dataInicio: undefined,
                            dataFim: undefined
                          }));
                        }
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-2 text-sm font-medium rounded-iris transition-colors ${
                        periodo.value === null
                          ? (filters.dias === null || filters.dataInicio || filters.dataFim)
                            ? 'bg-accent text-white'
                            : 'bg-surface-2 text-tx-2 hover:bg-surface'
                          : filters.dias === periodo.value && !filters.dataInicio && !filters.dataFim
                            ? 'bg-accent text-white'
                            : 'bg-surface-2 text-tx-2 hover:bg-surface'
                      }`}
                    >
                      {periodo.label}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({ dias: 30 });
                    setCurrentPage(1);
                  }}
                  className={btnSecundario}
                >
                  Limpar
                </button>
              </div>
            </div>

            {/* Intervalo de Datas Personalizado */}
            {(filters.dias === null || filters.dataInicio || filters.dataFim) && (
              <div className="flex flex-wrap items-center gap-3 mt-4 p-3 bg-surface-2 rounded-iris">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-tx-2">De:</label>
                  <input
                    type="date"
                    value={filters.dataInicio || ''}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        dataInicio: e.target.value || undefined,
                        dias: null
                      }));
                      setCurrentPage(1);
                    }}
                    className={inputIris}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-tx-2">Até:</label>
                  <input
                    type="date"
                    value={filters.dataFim || ''}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        dataFim: e.target.value || undefined,
                        dias: null
                      }));
                      setCurrentPage(1);
                    }}
                    className={inputIris}
                  />
                </div>
                {(filters.dataInicio || filters.dataFim) && (
                  <button
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        dataInicio: undefined,
                        dataFim: undefined,
                        dias: 30
                      }));
                      setCurrentPage(1);
                    }}
                    className="text-sm text-tx-3 hover:text-tx-2 underline"
                  >
                    Limpar datas
                  </button>
                )}
              </div>
            )}

            {/* Filtros Avançados */}
            {showFilters && (
              <div className="border-t border-line pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Valor Mínimo
                    </label>
                    <input
                      type="number"
                      placeholder="R$ 0,00"
                      className={`${inputIris} w-full`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Valor Máximo
                    </label>
                    <input
                      type="number"
                      placeholder="R$ 999.999,99"
                      className={`${inputIris} w-full`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Tipo de Frete
                    </label>
                    <select className={`${inputIris} w-full`}>
                      <option value="">Todos</option>
                      <option value="0">CIF (Emitente)</option>
                      <option value="1">FOB (Destinatário)</option>
                      <option value="2">Por conta terceiros</option>
                      <option value="3">Próprio remetente</option>
                      <option value="4">Próprio destinatário</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Status
                    </label>
                    <select className={`${inputIris} w-full`}>
                      <option value="">Todos</option>
                      <option value="autorizado">Autorizado</option>
                      <option value="cancelado">Cancelado</option>
                      <option value="inutilizado">Inutilizado</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setShowFilters(false)}
                    className={btnSecundario}
                  >
                    Fechar Filtros
                  </button>
                  <button
                    onClick={() => {
                      // Aplicar filtros avançados aqui
                      setShowFilters(false);
                    }}
                    className={btnPrimario}
                  >
                    Aplicar Filtros
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lista de CTes */}
        <div>
          <CteList
            xmlCtes={xmlCtes}
            onSelect={handleOpenModal}
            isLoading={isLoading}
          />
        </div>

        {/* Paginação e Controles */}
        <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-4 lg:p-6">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-sm text-tx-2 order-2 sm:order-1">
                Mostrando <span className="font-medium">{startIndex + 1}</span> a <span className="font-medium">{Math.min(startIndex + itemsPerPage, totalItems)}</span> de <span className="font-medium">{totalItems}</span> registros
              </div>

              <div className="flex flex-wrap items-center gap-2 order-1 sm:order-2">
                <label className="text-sm text-tx-2 whitespace-nowrap">Mostrar:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className={inputIris}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span className="text-sm text-tx-2 whitespace-nowrap">por página</span>
              </div>
            </div>

            {/* Controles de paginação */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="hidden sm:inline-flex px-3 py-2 text-sm border border-line rounded-iris text-tx hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Primeira
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-line rounded-iris text-tx hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Página anterior"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <div className="px-3 py-2 bg-accent-soft text-accent-text rounded-iris text-sm font-medium min-w-0">
                    <span className="whitespace-nowrap">{currentPage} / {totalPages}</span>
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-line rounded-iris text-tx hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Próxima página"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="hidden sm:inline-flex px-3 py-2 text-sm border border-line rounded-iris text-tx hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Última
                  </button>
                </div>

                {/* Navegação rápida mobile */}
                <div className="sm:hidden flex items-center gap-2">
                  <span className="text-xs text-tx-3">Ir para:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = Number(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        setCurrentPage(page);
                      }
                    }}
                    className={`${inputIris} w-16 px-2 text-center`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedCte && (
        <CteModal
          cte={selectedCte}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CteMonitoringPage;